import { Select as SelectPrimitive } from 'radix-ui'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

function SelectTrigger({
    className,
    size = 'default',
    children,
    ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & { size?: 'sm' | 'default' }) {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                'flex w-fit items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-field px-2.5 py-1 text-sm shadow-xs',
                // The default size is the primary data-entry target, so it clears 40px on a
                // coarse pointer. `sm` is a dense inline variant and keeps its height.
                size === 'sm' ? 'h-7' : 'h-8 pointer-coarse:h-10',
                'transition-[color,box-shadow,border-color] duration-(--animate-duration)',
                'data-[placeholder]:text-muted-foreground',
                // Field focus: the border turning solid indigo is the indicator; the soft halo is decoration.
                'outline-hidden focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25',
                'aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20',
                'aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive/25',
                'disabled:cursor-not-allowed disabled:bg-surface-raised disabled:opacity-50 disabled:shadow-none',
                "*:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}>
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="size-3.5 shrink-0 text-subtle-foreground" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectContent({
    className,
    children,
    position = 'popper',
    ...props
}: ComponentProps<typeof SelectPrimitive.Content>) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                position={position}
                className={cn(
                    'relative z-50 max-h-(--radix-select-content-available-height) min-w-32 overflow-y-auto overflow-x-hidden',
                    // Same shell as the dropdown menu: 8px radius, hairline, popover fill, md shadow.
                    'rounded-lg border border-border bg-popover text-popover-foreground shadow-md',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
                    className
                )}
                {...props}>
                <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center">
                    <ChevronUpIcon className="size-4" />
                </SelectPrimitive.ScrollUpButton>
                <SelectPrimitive.Viewport
                    className={cn('p-1', position === 'popper' && 'min-w-(--radix-select-trigger-width)')}>
                    {children}
                </SelectPrimitive.Viewport>
                <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center">
                    <ChevronDownIcon className="size-4" />
                </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    )
}

function SelectLabel({ className, ...props }: ComponentProps<typeof SelectPrimitive.Label>) {
    return (
        <SelectPrimitive.Label
            data-slot="select-label"
            className={cn('px-2 py-1.5 text-2xs font-medium text-muted-foreground', className)}
            {...props}
        />
    )
}

function SelectItem({ className, children, ...props }: ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                // Matching the dropdown and context menus: a select option is a thumb
                // target on a phone, so the row grows below sm and desktop keeps py-1.5.
                'relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1.5 pl-2 pr-8 text-sm outline-none max-sm:py-2.5',
                'transition-colors duration-(--animate-duration-fast)',
                'focus:bg-accent focus:text-accent-foreground',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
                className
            )}
            {...props}>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
            <span className="absolute right-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
        </SelectPrimitive.Item>
    )
}

function SelectSeparator({ className, ...props }: ComponentProps<typeof SelectPrimitive.Separator>) {
    return <SelectPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
}

export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue }
